// src/lib/managers/conversation/turnError.ts

/**
 * Tour raté de l'assistant (F-1.17) : sa cause et sa réponse. Un tour raté ne laisse rien dans
 * l'historique ; le SDK rend une réponse `Error` qui porte la cause, que le web et le mobile
 * affichent dans le fil avec « Réessayer », sauf pour un quota épuisé.
 */

import { generateUUID } from "../../utils/uuid/generateUUID";
import { ApiFunctionalError, ApiNetworkError } from "../api/errors";
import { buildTextResponse } from "./textResponse";
import {
    ComponentData,
    ComponentDataContentType,
    ContentAction,
    TurnErrorCause,
} from "./types/conversation";

/** Texte d'un tour raté, par cause (lu tel quel par une app qui ne traduit pas encore la cause). */
const TURN_ERROR_MESSAGES: Record<TurnErrorCause, string> = {
    quota: "Vous avez atteint votre quota d'utilisation de l'assistant.",
    network:
        "Connexion impossible : vérifiez votre connexion internet, puis réessayez.",
    service:
        "L'assistant est momentanément indisponible, réessayez dans un instant.",
};

/** Messages d'un `fetch` resté sans réponse : Chrome, Firefox, React Native. */
const NETWORK_FAILURE_MESSAGES = [
    "Failed to fetch",
    "NetworkError when attempting to fetch resource",
    "Network request failed",
];

/** Safari : message trop générique pour être cherché ailleurs que dans le `TypeError` de `fetch`. */
const SAFARI_NETWORK_FAILURE_MESSAGE = "Load failed";

const MAX_CAUSE_DEPTH = 10;

/** L'erreur et ses causes successives (`error.cause`), de la plus externe à la plus interne. */
const causeChain = (error: unknown): unknown[] => {
    const chain: unknown[] = [];
    let current: unknown = error;
    while (
        current !== undefined &&
        current !== null &&
        !chain.includes(current) &&
        chain.length < MAX_CAUSE_DEPTH
    ) {
        chain.push(current);
        current = (current as { cause?: unknown }).cause;
    }
    return chain;
};

/**
 * Quota d'assistant épuisé : 429 de l'Api-Backend (`ApiFunctionalError`, avec ou sans code). Un 429
 * de Graph ou de Google (saturation passagère) n'en est pas un. Le code d'erreur que l'Api-Backend
 * rend pour un quota épuisé n'est pas encore connu (demande à l'équipe Api-Backend) : à ajouter ici.
 */
const isQuotaError = (error: unknown): boolean =>
    error instanceof ApiFunctionalError && error.status === 429;

const isNetworkFailure = (error: unknown): boolean => {
    if (error instanceof ApiNetworkError) {
        return true;
    }
    if (!(error instanceof Error)) {
        return false;
    }
    if (
        error instanceof TypeError &&
        error.message.includes(SAFARI_NETWORK_FAILURE_MESSAGE)
    ) {
        return true;
    }
    return NETWORK_FAILURE_MESSAGES.some((message) =>
        error.message.includes(message),
    );
};

/** Statuts d'une panne passagère derrière l'Api-Backend : la même requête peut être rejouée. */
const REPLAYABLE_STATUSES = new Set([502, 503, 504]);

/**
 * Un appel du tour qui peut être rejoué tel quel (mêmes messages, même état du tour) : réseau coupé,
 * ou service derrière l'Api-Backend momentanément en panne. Jamais un 4xx ni un quota.
 */
export function isReplayableCallError(error: unknown): boolean {
    return causeChain(error).some(
        (cause) =>
            (cause instanceof ApiNetworkError && !isAbort(cause)) ||
            (cause instanceof ApiFunctionalError &&
                REPLAYABLE_STATUSES.has(cause.status)),
    );
}

/** Requête abandonnée par l'app (tour annulé) : ni une panne, ni à rejouer. */
export const isAbort = (error: unknown): boolean =>
    causeChain(error).some(
        (cause) =>
            cause instanceof Error &&
            (cause.name === "AbortError" ||
                cause.name === "TurnCancelledError"),
    );

/** Tour annulé pendant qu'il tournait (« Nouvelle conversation ») : rien n'en sort. */
export class TurnCancelledError extends Error {
    public constructor() {
        super("tour annulé");
        this.name = "TurnCancelledError";
        Object.setPrototypeOf(this, TurnCancelledError.prototype);
    }
}

/**
 * `promise`, ou un rejet `TurnCancelledError` dès que le tour est arrêté : une étape lente (une
 * recherche de contact qui attend le RAG puis le carnet) ne retient pas « Arrêter ». L'étape continue
 * en arrière-plan ; son résultat est ignoré.
 */
export function untilCancelled<T>(
    promise: Promise<T>,
    signal: AbortSignal,
): Promise<T> {
    if (signal.aborted) return Promise.reject(new TurnCancelledError());
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(new TurnCancelledError());
        signal.addEventListener("abort", onAbort, { once: true });
        promise.then(
            (value) => {
                signal.removeEventListener("abort", onAbort);
                resolve(value);
            },
            (error: unknown) => {
                signal.removeEventListener("abort", onAbort);
                reject(error);
            },
        );
    });
}

/** Cause d'un tour raté, lue dans l'erreur et ses causes successives. */
export function classifyTurnError(error: unknown): TurnErrorCause {
    const chain = causeChain(error);
    if (chain.some(isQuotaError)) {
        return "quota";
    }
    if (chain.some(isNetworkFailure)) {
        return "network";
    }
    return "service";
}

/** Tour arrêté par l'utilisateur (« Arrêter ») : rien à afficher, la question sort de l'historique. */
export const buildStoppedResponse = (): ComponentData => ({
    ...buildCancelledResponse(),
    stopped: true,
});

/** Tour annulé (conversation remise à zéro pendant le tour) : rien à afficher, rien à garder. */
export const buildCancelledResponse = (): ComponentData => ({
    ...buildTextResponse(""),
    cancelled: true,
});

/** Réponse d'un tour raté hors des outils (Api-Ai injoignable, réponse illisible…). */
export const buildTurnErrorResponse = (
    cause: TurnErrorCause,
): ComponentData => ({
    aiComment: TURN_ERROR_MESSAGES[cause],
    userRequestId: generateUUID(),
    content: {
        contentData: "",
        contentType: ComponentDataContentType.Error,
        contentAction: ContentAction.VIEW,
        upcomingAnalysis: false,
    },
    aiVocalText: TURN_ERROR_MESSAGES[cause],
    errorCause: cause,
});
